import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import IOwTableCols from "../../../models/IOwTableCols";
import IVehicle from "../../../models/IVehicle";
import { OwFnTable } from "../../shared/OwFnTable/OwFnTable";
import PaginationBar from "../../shared/PaginationBar/PaginationBar";
import { ConfirmDeleteOverlayFn } from "../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import { buildRestUrl, buildRestUrlWithQuery, buildAuthHeadersForFetch } from "../../../services/RestService";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import IOnGetVehiclesDTO from "../../../models/DTO/IOnGetVehiclesDTO";
import { VehicleSidebar } from "./VehicleSidebar/VehicleSidebar";
import { useAppContextNonNull } from "@contexts/AppContext";
import "./Vehicles.scss";

export const Vehicles = () => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();

    const getBreadcrumbs = (): IBreadcrumbs[] => {
        return [
            {
                title: "daily_routes",
                url: "/routes/daily",
            },
            {
                title: "vehicles",
                url: "/routes/vehicles",
            },
        ];
    };

    const openVehicleSidebar = (row?: IVehicle) => {
        const sideBarComponent = <VehicleSidebar cancelFn={cancelSidebar} vehicle={row} />;

        uiContext.setSidebarComponent(sideBarComponent);
        uiContext.toggleSidebar(true);
    };

    const cancelSidebar = (refresh?: boolean) => {
        if (refresh) {
            getVehicles();
        }

        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);
    };

    const showDeleteConfirm = (row: IVehicle, index: number) => {
        setTargetElem(`row-${index}`);
        setSelectedVehicle(row);
    };

    const renderActions = (row: IVehicle, index: number) => {
        return (
            <OwDeleteEditActions
                isEditMode={false}
                onTrashClickFn={() => {
                    showDeleteConfirm(row, index);
                }}
                onEditClickFn={() => {
                    openVehicleSidebar(row);
                }}
            />
        );
    };

    const renderName = (row: IVehicle) => {
        return (
            <div className={"vehicle-name-cell"}>
                <div>{row.name}</div>
                {/* Vehicle par lives on the van warehouse, which only exists with truck inventory. */}
                {supplierModel?.has_par_levels && supplierModel?.has_truck_inventory ? (
                    <Link to={`/vehicles/${row.guid}/detail`} className={"vehicle-detail-link"}>
                        <OwLabel title={"detail_view"} noBottomMargin={true} />
                    </Link>
                ) : null}
            </div>
        );
    };

    const renderDefaultRoute = (row: IVehicle) => {
        return <div>{row.Default_Route?.name || "-"}</div>;
    };

    const getTableCols = (): IOwTableCols[] => {
        return [
            {
                title: "",
                valueProp: "actions",
                renderFn: renderActions,
                renderFnEmitsRow: true,
            },
            {
                title: "name",
                valueProp: "name",
                renderFn: renderName,
                renderFnEmitsRow: true,
            },
            {
                title: "default_route",
                valueProp: "default_route",
                renderFn: renderDefaultRoute,
                renderFnEmitsRow: true,
            },
        ];
    };

    const [crumbs] = useState<IBreadcrumbs[]>(getBreadcrumbs());
    const [tableCols] = useState(getTableCols());
    const [isDataFetching, setIsDataFetching] = useState(true);
    const [isInitialData, setIsInitialData] = useState(true);
    const [vehicles, setVehicles] = useState<IVehicle[]>([]);
    const [totalVehicles, setTotalVehicles] = useState<number>(0);

    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(25);

    const [targetElem, setTargetElem] = useState<string | null>(null);
    const [selectedVehicle, setSelectedVehicle] = useState<IVehicle | undefined>();

    const getVehicles = async () => {
        setIsDataFetching(true);

        const url = buildRestUrlWithQuery({
            endpoint: RestEndpointsEnum.GetVehicles,
            query: { limit, offset },
        });

        const headers = buildAuthHeadersForFetch({
            userModel,
            supplierModel,
        });

        try {
            const response = await fetch(url, { method: "GET", headers });
            const data: IOnGetVehiclesDTO = await response.json();

            setVehicles(data.vehicles);
            setTotalVehicles(data.totalVehicles);
        } catch (error) {
            console.error("Error fetching vehicles:", error);
        } finally {
            setIsDataFetching(false);
            setIsInitialData(false);
        }
    };

    const deleteVehicle = async () => {
        if (!selectedVehicle) return;

        const url = buildRestUrl({
            endpoint: RestEndpointsEnum.DeleteVehicle,
            params: { vehicleGuid: selectedVehicle.guid },
        });

        const headers = buildAuthHeadersForFetch({
            userModel,
            supplierModel,
        });

        try {
            await fetch(url, { method: "DELETE", headers });
            setTargetElem(null);
            setSelectedVehicle(undefined);
            getVehicles();
        } catch (error) {
            console.error("Error deleting vehicle:", error);
        }
    };

    useEffect(() => {
        getVehicles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, limit]);

    const onPaginationChanged = (_offset: number, _limit: number) => {
        setOffset(_offset);
        setLimit(_limit);
    };

    const handleConfirm = () => {
        deleteVehicle();
    };

    const handleCancel = () => {
        setTargetElem(null);
        setSelectedVehicle(undefined);
    };

    return (
        <div className={"Vehicles position-relative"} id={"vehicles"}>
            <Breadcrumbs crumbs={crumbs} />

            <div className="row mt-2">
                <div className={"col-12 d-flex align-items-center"}>
                    <OwTitle title={"vehicles"} leftIconName={"fad fa-cars"} />
                    <div>
                        <OwButton
                            className={"ms-2 btn-warning"}
                            title={"create"}
                            leftIconClass={"fas fa-plus"}
                            fn={() => {
                                openVehicleSidebar();
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className={"position-relative"}>
                <ConfirmDeleteOverlayFn
                    targetElem={targetElem}
                    handleConfirm={handleConfirm}
                    handleCancel={handleCancel}
                    leftAdjustment={0}
                    nestedParentElem={"vehicles-table"}
                    skipMainView={true}
                />
                <OwFnTable
                    id={"vehicles-table"}
                    columns={tableCols}
                    dataSet={vehicles}
                    dataFetch={isDataFetching}
                    initialData={isInitialData}
                />
            </div>

            <div className="mt-2">
                <PaginationBar offset={offset} limit={limit} totalRecords={totalVehicles} onPaginationChanged={onPaginationChanged} />
            </div>
        </div>
    );
};
